import {FC, useEffect, useMemo, useState} from 'react'
import {api, Announcement, AnnouncementInput, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {confirmDialog} from '../../components/dialog'
import {PageHead} from '../../components/PageHead'
import {ListToolbar, FilterSelect} from '../../components/ListToolbar'
import {useIsMobile} from '../../hooks/useIsMobile'
import {createPortal} from 'react-dom'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const AnnouncementsPage: FC = () => {
  const toast = useToast()
  const [anns, setAnns] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [editAnn, setEditAnn] = useState<{a: Announcement | null} | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [pinnedFilter, setPinnedFilter] = useState('')
  const isMobile = useIsMobile()

  const load = async () => {
    setLoading(true)
    try {
      setAnns(await api.adminAnnouncements())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return anns.filter((a) => {
      if (q && !`${a.title} ${a.body}`.toLowerCase().includes(q)) return false
      if (statusFilter === 'published' && !a.published) return false
      if (statusFilter === 'draft' && a.published) return false
      if (pinnedFilter === 'pinned' && !a.pinned) return false
      if (pinnedFilter === 'unpinned' && a.pinned) return false
      return true
    })
  }, [anns, search, statusFilter, pinnedFilter])

  const delAnn = async (a: Announcement) => {
    if (!(await confirmDialog({title: `Delete "${a.title}"?`, text: 'This removes the announcement.', confirmText: 'Delete', danger: true}))) return
    setBusyId(a.id)
    try {
      await api.deleteAnnouncement(a.id)
      setAnns((l) => l.filter((x) => x.id !== a.id))
      toast.success('Announcement deleted.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not delete')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <PageHead icon='📣' eyebrow='Content' title='Announcements' sub='Broadcast updates and news to renters.'
        action={<button onClick={() => setEditAnn({a: null})} style={btnHeader}>+ New announcement</button>} />

      <ListToolbar search={search} onSearch={setSearch} placeholder='Search announcements…'>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} ariaLabel='Status'
          options={[{label: 'All', value: ''}, {label: 'Published', value: 'published'}, {label: 'Draft', value: 'draft'}]} />
        <FilterSelect value={pinnedFilter} onChange={setPinnedFilter} ariaLabel='Pinned'
          options={[{label: 'All', value: ''}, {label: 'Pinned', value: 'pinned'}, {label: 'Unpinned', value: 'unpinned'}]} />
      </ListToolbar>

      {loading ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
      ) : (
        <section>
          <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: '0 0 14px'}}>Announcements</h2>
          {filtered.length === 0 ? (
            <div style={{background: '#fff', borderRadius: 14, padding: 30, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>{anns.length === 0 ? 'No announcements yet.' : 'No announcements match your filters.'}</div>
          ) : (
            <div className='ccp-stagger' style={{display: 'flex', flexDirection: 'column', gap: 12}}>
              {filtered.map((a) => (
                <div key={a.id} className='ccp-lift' style={{background: '#fff', borderRadius: 14, padding: '16px 20px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', borderLeft: a.pinned ? `4px solid ${ACCENT}` : '4px solid transparent'}}>
                  <div style={{display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-start', justifyContent: 'space-between', gap: 12}}>
                    <div style={{minWidth: 0}}>
                      <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <span style={{fontSize: 16, fontWeight: 700, color: INK}}>{a.title}</span>
                        {a.pinned && <Tag color={ACCENT} bg='rgba(3,149,64,0.12)'>Pinned</Tag>}
                        {a.published ? <Tag color='#1f9d5b' bg='rgba(3,149,64,0.12)'>Published</Tag> : <Tag color={MUTED} bg='rgba(126,130,153,0.15)'>Draft</Tag>}
                      </div>
                      <div style={{fontSize: 14, color: '#475569', whiteSpace: 'pre-wrap'}}>{a.body}</div>
                    </div>
                    <div style={{display: 'flex', gap: 8, flexShrink: 0}}>
                      <button onClick={() => setEditAnn({a})} disabled={busyId === a.id} style={{...btnGhost, ...(isMobile ? {flex: 1} : {})}}>Edit</button>
                      <button onClick={() => delAnn(a)} disabled={busyId === a.id} style={{...btnDanger, ...(isMobile ? {flex: 1} : {})}}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {editAnn && (
        <AnnouncementModal
          ann={editAnn.a}
          onClose={() => setEditAnn(null)}
          onSaved={(saved, isNew) => {
            setAnns((l) => (isNew ? [saved, ...l] : l.map((x) => (x.id === saved.id ? saved : x))))
            setEditAnn(null)
            toast.success(`Announcement ${isNew ? 'created' : 'saved'}.`)
          }}
        />
      )}
    </div>
  )
}

const Tag: FC<{color: string; bg: string; children: React.ReactNode}> = ({color, bg, children}) => (
  <span style={{fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '3px 8px', color, background: bg}}>{children}</span>
)

const AnnouncementModal: FC<{ann: Announcement | null; onClose: () => void; onSaved: (a: Announcement, isNew: boolean) => void}> = ({ann, onClose, onSaved}) => {
  const isNew = !ann
  const [form, setForm] = useState<AnnouncementInput>({title: ann?.title ?? '', body: ann?.body ?? '', pinned: ann?.pinned ?? false, published: ann?.published ?? true})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    setErr(null)
    if (form.title.trim().length < 3) return setErr('Title must be at least 3 characters.')
    if (form.body.trim().length < 1) return setErr('Body is required.')
    setBusy(true)
    try {
      const saved = isNew ? await api.createAnnouncement(form) : await api.updateAnnouncement(ann!.id, form)
      onSaved(saved, isNew)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save')
      setBusy(false)
    }
  }

  return (
    <Modal title={isNew ? 'New announcement' : 'Edit announcement'} onClose={onClose}>
      {err && <ErrBox>{err}</ErrBox>}
      <Field label='Title'><input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder='e.g. Water shutoff Saturday' style={inp} /></Field>
      <Field label='Body'><textarea value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} placeholder='Write the announcement…' style={{...inp, minHeight: 130, resize: 'vertical'}} /></Field>
      <div style={{display: 'flex', gap: 20, marginBottom: 4}}>
        <Check label='Pinned' checked={!!form.pinned} onChange={(v) => setForm({...form, pinned: v})} />
        <Check label='Published' checked={!!form.published} onChange={(v) => setForm({...form, published: v})} />
      </div>
      <Footer onClose={onClose} onSave={save} busy={busy} saveLabel={isNew ? 'Publish' : 'Save'} />
    </Modal>
  )
}

// ─── Small shared modal pieces ──────────────────────────────────────────
const Modal: FC<{title: string; onClose: () => void; children: React.ReactNode}> = ({title, onClose, children}) => createPortal(
  <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto'}}>
    <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 24px', width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
      <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 18px'}}>{title}</h2>
      {children}
    </div>
  </div>,
  document.body,
)
const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{marginBottom: 14}}>
    <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)
const Check: FC<{label: string; checked: boolean; onChange: (v: boolean) => void}> = ({label, checked, onChange}) => (
  <label style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: INK, cursor: 'pointer'}}>
    <input type='checkbox' checked={checked} onChange={(e) => onChange(e.target.checked)} style={{width: 16, height: 16, accentColor: ACCENT}} />
    {label}
  </label>
)
const ErrBox: FC<{children: React.ReactNode}> = ({children}) => (
  <div style={{background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 14}}>{children}</div>
)
const Footer: FC<{onClose: () => void; onSave: () => void; busy: boolean; saveLabel: string}> = ({onClose, onSave, busy, saveLabel}) => (
  <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, paddingTop: 16, borderTop: '1px solid #f1f3f7'}}>
    <button onClick={onClose} style={btnGhost} disabled={busy}>Cancel</button>
    <button onClick={onSave} style={{...btnPrimary, minWidth: 110}} disabled={busy}>{busy ? <Spinner size={14} /> : saveLabel}</button>
  </div>
)

const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnHeader: React.CSSProperties = {...btnBase, background: '#fff', color: ACCENT, fontWeight: 700, padding: '10px 16px'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

export {AnnouncementsPage}
